// PocketBase realtime subscriptions (SSE on /api/realtime), used as
// invalidation signals only: handlers refetch through the /v1 api modules
// rather than consuming the record payload (the PB record shape is not the /v1
// wire shape). Delivery is authorized server-side by the collection's
// list/view rules (migration 0032).

import { pb } from "./client.js";

export interface LiveOptions {
  collection: string;
  /** A record id, or "*" for the whole collection. */
  topic: string;
  /** Server-side event filter; build with pb.filter() when interpolating. */
  filter?: string;
  /** Comma-separated payload fields — keep events small, e.g. "id,status". */
  fields?: string;
  onEvent: (action: string, id: string) => void;
  /** Subscription established — do a one-shot refetch to close the setup gap. */
  onUp?: () => void;
  /** Could not establish (transport/submit failure) — start the poll fallback.
   * Note: a rule that denies access does NOT reject; it silently delivers no
   * events, so onDown only covers transport failures, not authorization. */
  onDown: () => void;
}

/**
 * subscribeLive subscribes to realtime changes and returns an idempotent
 * teardown. Safe to tear down before the subscription resolves; the SDK
 * auto-reconnects an established stream and re-submits on reconnect.
 */
export function subscribeLive(opts: LiveOptions): () => void {
  let stopped = false;
  let unsubscribe: (() => void) | undefined;

  pb.collection(opts.collection)
    .subscribe(opts.topic, (e) => opts.onEvent(e.action, e.record.id), {
      filter: opts.filter,
      fields: opts.fields,
    })
    .then((unsub) => {
      if (stopped) {
        void unsub();
        return;
      }
      unsubscribe = () => void unsub();
      opts.onUp?.();
    })
    .catch(() => {
      if (!stopped) opts.onDown();
    });

  return () => {
    stopped = true;
    unsubscribe?.();
    unsubscribe = undefined;
  };
}
